import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ServiceFee {
  id: string;
  seller_fee_percent: number;
  buyer_fee_percent: number;
  provider_fee_percent: number;
  requester_fee_percent: number;
  renter_fee_percent: number;
  rental_provider_fee_percent: number;
  start_date: string;
  end_date: string | null;
  in_use: boolean;
  created_at: string;
  updated_at: string;
  verification_fee: number;
  verification_fee_currency: string;
  verification_fee_currency_symbol: string;
  country_name: string;
}

const ServiceFeeEdit: React.FC = () => {
  const navigate = useNavigate();
  const { adminRole } = useAuth();
  const [serviceFees, setServiceFees] = useState<ServiceFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ServiceFee>>({});

  useEffect(() => {
    if (adminRole !== 'super_admin') {
      navigate('/service-fees');
      return;
    }
    fetchServiceFees();
  }, [adminRole, navigate]);

  const fetchServiceFees = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('servicefee')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Failed to fetch service fees');
        return;
      }

      setServiceFees(data || []);
    } catch (err) {
      setError('Failed to fetch service fees');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (fee: ServiceFee) => {
    setEditingId(fee.id);
    setEditForm({
      seller_fee_percent: fee.seller_fee_percent,
      buyer_fee_percent: fee.buyer_fee_percent,
      provider_fee_percent: fee.provider_fee_percent,
      requester_fee_percent: fee.requester_fee_percent,
      renter_fee_percent: fee.renter_fee_percent,
      rental_provider_fee_percent: fee.rental_provider_fee_percent,
      verification_fee: fee.verification_fee,
      verification_fee_currency: fee.verification_fee_currency,
      verification_fee_currency_symbol: fee.verification_fee_currency_symbol,
      in_use: fee.in_use,
      start_date: fee.start_date,
      end_date: fee.end_date,
    });
  };

  const handleSave = async (id: string) => {
    try {
      const { error } = await supabase
        .from('servicefee')
        .update({
          ...editForm,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        alert('Failed to update service fee');
        return;
      }

      // Refresh the data
      await fetchServiceFees();
      setEditingId(null);
      setEditForm({});
      alert('Service fee updated successfully');
    } catch (err) {
      alert('Failed to update service fee');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleInputChange = (field: keyof ServiceFee, value: any) => {
    setEditForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      // Auto-update currency symbol when currency changes
      if (field === 'verification_fee_currency') {
        newForm.verification_fee_currency_symbol = getCurrencySymbol(value);
      }
      
      return newForm;
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const formatCurrency = (amount: number, currency: string, symbol: string) => {
    return `${symbol}${amount.toFixed(2)} ${currency.toUpperCase()}`;
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'usd': '$',
      'eur': '€',
      'gbp': '£',
      'aud': 'A$',
      'cad': 'C$',
    };
    return symbols[currency] || '$';
  };

  if (adminRole !== 'super_admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading service fees...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchServiceFees}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Edit Service Fees</h2>
              <p className="text-green-100 mt-1">Edit service fee configurations by country</p>
            </div>
            <button
              onClick={() => navigate('/service-fees')}
              className="flex items-center px-4 py-2 text-white bg-[#008f6b] rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Back to Service Fees
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        {/* Service Fees Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Service Fee Configurations (Edit Mode)
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Click on any field to edit. Changes are saved when you click Save.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seller Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requester Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Renter Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rental Provider Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verification Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serviceFees.map((fee) => (
                  <tr key={fee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-indigo-600">
                              {fee.country_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{fee.country_name}</div>
                          <div className="text-sm text-gray-500">{fee.verification_fee_currency.toUpperCase()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.seller_fee_percent || 0}
                          onChange={(e) => handleInputChange('seller_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.seller_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.buyer_fee_percent || 0}
                          onChange={(e) => handleInputChange('buyer_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.buyer_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.provider_fee_percent || 0}
                          onChange={(e) => handleInputChange('provider_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.provider_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.requester_fee_percent || 0}
                          onChange={(e) => handleInputChange('requester_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.requester_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.renter_fee_percent || 0}
                          onChange={(e) => handleInputChange('renter_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.renter_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editForm.rental_provider_fee_percent || 0}
                          onChange={(e) => handleInputChange('rental_provider_fee_percent', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{fee.rental_provider_fee_percent}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editForm.verification_fee || 0}
                            onChange={(e) => handleInputChange('verification_fee', parseFloat(e.target.value))}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <select
                            value={editForm.verification_fee_currency || ''}
                            onChange={(e) => handleInputChange('verification_fee_currency', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="usd">USD</option>
                            <option value="eur">EUR</option>
                            <option value="gbp">GBP</option>
                            <option value="aud">AUD</option>
                            <option value="cad">CAD</option>
                          </select>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-900">
                          {formatCurrency(fee.verification_fee, fee.verification_fee_currency, fee.verification_fee_currency_symbol)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <select
                          value={editForm.in_use ? 'true' : 'false'}
                          onChange={(e) => handleInputChange('in_use', e.target.value === 'true')}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          fee.in_use 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {fee.in_use ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === fee.id ? (
                        <input
                          type="date"
                          value={formatDate(editForm.start_date || null)}
                          onChange={(e) => handleInputChange('start_date', e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-500">{new Date(fee.start_date).toLocaleDateString()}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingId === fee.id ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSave(fee.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(fee)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {serviceFees.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                No service fee configurations found.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceFeeEdit;
